import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight, Compass, Target, CalendarClock, ShieldCheck,
  Gauge, BrainCircuit, Radar, Route as RouteIcon, PlayCircle, Check,
} from "lucide-react";
import { PublicNav } from "@/components/PublicNav";
import { OrbitMap } from "@/components/OrbitMap";
import { OrbitWordmark, OrbitLogo } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { toast } from "@/components/ui/sonner";

const PREVIEW_DOMAINS = [
  { key: "customers", label: "Customers", score: 55, status: "Developing" },
  { key: "sales", label: "Sales", score: 40, status: "At Risk" },
  { key: "operations", label: "Operations", score: 58, status: "Developing" },
  { key: "finance", label: "Finance", score: 66, status: "Strong" },
  { key: "people", label: "People", score: 50, status: "Developing" },
  { key: "technology", label: "Technology", score: 45, status: "At Risk" },
  { key: "data", label: "Data", score: 38, status: "At Risk" },
  { key: "ai", label: "AI", score: 30, status: "Opportunity" },
];

const VALUE_PROPS = [
  { icon: Compass, title: "Clarity first, not AI hype", body: "ORBIT translates technology into business language and shows you where value actually is — before anyone talks about tools." },
  { icon: Target, title: "Built for MSME reality", body: "Aware of capital limits, lean teams and legacy systems. Recommendations you can realistically act on, not enterprise theory." },
  { icon: CalendarClock, title: "90-day execution focus", body: "Every diagnosis ends in a practical roadmap: what to do in the next 30, 60 and 90 days — and what to do first." },
];

const STAGES = [
  { n: "01", label: "Welcome", body: "Tell ORBIT about your business in a few guided minutes." },
  { n: "02", label: "Understand", body: "An intelligent conversation across 10 areas of your business." },
  { n: "03", label: "Discover", body: "A live business map takes shape as you answer." },
  { n: "04", label: "Diagnose", body: "Deterministic Digital Maturity and AI Readiness scores you can trust." },
  { n: "05", label: "Prioritise", body: "High-value opportunities ranked by impact, effort and readiness." },
  { n: "06", label: "Transform", body: "A practical 90-day roadmap and a professional report." },
];

const RECEIVE = [
  { icon: Gauge, label: "Digital Maturity Score" },
  { icon: BrainCircuit, label: "AI Readiness Score" },
  { icon: Radar, label: "Opportunity Radar" },
  { icon: RouteIcon, label: "90-Day Roadmap" },
];

export default function Landing() {
  const navigate = useNavigate();
  const { demoLogin } = useAuth();
  const [demoLoading, setDemoLoading] = useState(false);

  const startDemo = async () => {
    setDemoLoading(true);
    const res = await demoLogin();
    setDemoLoading(false);
    if (res.ok) {
      toast.success("Demonstration company loaded", { description: "Exploring Aarohan Precision Components" });
      navigate("/app");
    } else {
      toast.error("Couldn't start the demo", { description: res.error });
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <PublicNav />

      {/* HERO */}
      <section className="relative overflow-hidden orbit-grid-bg">
        <div className="pointer-events-none absolute -right-40 top-[-10rem] h-[36rem] w-[36rem] rounded-full bg-accent/10 blur-3xl" />
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-4 py-16 sm:px-8 lg:grid-cols-2 lg:gap-8 lg:px-12 lg:py-24">
          <div className="orbit-rise">
            <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5 text-xs font-semibold text-muted-foreground">
              <span className="h-2 w-2 rounded-full bg-accent" />
              AI Digital Transformation Copilot for MSMEs
            </div>
            <h1 className="font-display text-4xl font-bold leading-[1.08] tracking-tight sm:text-5xl lg:text-6xl">
              Know where your business should <span className="text-accent">transform next.</span>
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg">
              ORBIT analyses how your business operates, evaluates digital and AI readiness, identifies high-value
              opportunities and turns them into a practical transformation roadmap.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Button size="lg" className="h-12 bg-accent px-7 text-base text-accent-foreground hover:bg-accent/90" onClick={() => navigate("/register")} data-testid="hero-start-btn">
                Start Your Assessment <ArrowRight className="h-4 w-4" />
              </Button>
              <Button size="lg" variant="outline" className="h-12 px-7 text-base" onClick={startDemo} disabled={demoLoading} data-testid="hero-demo-btn">
                <PlayCircle className="h-4 w-4" /> {demoLoading ? "Loading demo…" : "Explore the Demo"}
              </Button>
            </div>
            <p className="mt-4 text-xs text-muted-foreground">
              Approximately 8–12 minutes · No credit card · Demo uses fictional company data
            </p>
          </div>

          <div className="orbit-rise flex justify-center lg:justify-end" style={{ animationDelay: "120ms" }}>
            <div className="w-full max-w-[460px] rounded-2xl border border-border bg-card p-6 shadow-xl shadow-black/5">
              <div className="mb-3 flex items-center justify-between">
                <div className="orbit-eyebrow">Live Business Map</div>
                <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-700 dark:text-amber-400">Illustrative</span>
              </div>
              <OrbitMap domains={PREVIEW_DOMAINS} size={400} />
              <div className="mt-4 flex flex-wrap gap-x-4 gap-y-2 text-xs text-muted-foreground">
                {[["Strong", "bg-emerald-600"], ["Developing", "bg-blue-600"], ["At Risk", "bg-red-600"], ["Opportunity", "bg-amber-600"]].map(([l, c]) => (
                  <span key={l} className="inline-flex items-center gap-1.5"><span className={`h-2 w-2 rounded-full ${c}`} />{l}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT YOU RECEIVE */}
      <section className="border-y border-border bg-card">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-6 px-4 py-8 sm:px-8 lg:px-12">
          <p className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">What you receive</p>
          <div className="flex flex-wrap gap-x-8 gap-y-4">
            {RECEIVE.map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-2.5">
                <Icon className="h-5 w-5 text-accent" />
                <span className="text-sm font-semibold">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY ORBIT */}
      <section id="why-orbit" className="mx-auto max-w-7xl px-4 py-20 sm:px-8 lg:px-12">
        <div className="max-w-2xl">
          <div className="orbit-eyebrow mb-3">Why ORBIT</div>
          <h2 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">
            ORBIT does not sell AI. It sells clarity about what technology should do next.
          </h2>
        </div>
        <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
          {VALUE_PROPS.map(({ icon: Icon, title, body }) => (
            <div key={title} className="rounded-2xl border border-border bg-card p-7 transition-colors duration-200 hover:border-accent/40">
              <div className="mb-5 flex h-11 w-11 items-center justify-center rounded-xl bg-accent/10">
                <Icon className="h-5 w-5 text-accent" />
              </div>
              <h3 className="font-display text-lg font-semibold">{title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" className="border-y border-border bg-card/40">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-8 lg:px-12">
          <div className="max-w-2xl">
            <div className="orbit-eyebrow mb-3">How ORBIT Works</div>
            <h2 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">A six-stage journey from curiosity to action.</h2>
            <p className="mt-3 text-muted-foreground">Welcome → Understand → Discover → Diagnose → Prioritise → Transform.</p>
          </div>
          <div className="mt-12 grid grid-cols-1 gap-px overflow-hidden rounded-2xl border border-border bg-border sm:grid-cols-2 lg:grid-cols-3">
            {STAGES.map((s) => (
              <div key={s.n} className="bg-card p-7">
                <span className="font-num text-sm font-semibold text-accent">{s.n}</span>
                <h3 className="mt-3 font-display text-lg font-semibold">{s.label}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT MYTHOS */}
      <section id="about" className="mx-auto max-w-7xl px-4 py-20 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2">
          <div>
            <div className="orbit-eyebrow mb-3">About MYTHIVA™</div>
            <h2 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">Built by Mythos India Studios.</h2>
            <p className="mt-4 text-base leading-relaxed text-muted-foreground">
              MYTHIVA™, by Mythos India Studios Private Limited, builds technology to create business value — not
              technology for technology's sake. ORBIT is our diagnostic and planning layer for Indian MSMEs: it
              understands your business, quantifies the priority, and gives you a path to implementation.
            </p>
            <ul className="mt-6 space-y-3">
              {["Deterministic, explainable scoring — never a black box", "Clear separation of your facts, calculations and AI insight", "Your product, your data — portable and yours to own"].map((t) => (
                <li key={t} className="flex items-start gap-3 text-sm">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-500/15"><Check className="h-3 w-3 text-emerald-600" /></span>
                  {t}
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl border border-border bg-gradient-to-br from-card to-secondary/50 p-10">
            <OrbitLogo size={56} className="text-foreground" />
            <blockquote className="mt-6 font-display text-xl font-medium leading-relaxed">
              "Now I understand my business better — and I finally know what technology I should implement next."
            </blockquote>
            <p className="mt-4 flex items-center gap-2 text-sm text-muted-foreground">
              <ShieldCheck className="h-4 w-4" /> The outcome every ORBIT assessment is designed to produce.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-border bg-primary text-primary-foreground">
        <div className="mx-auto flex max-w-7xl flex-col items-start gap-6 px-4 py-16 sm:px-8 lg:flex-row lg:items-center lg:justify-between lg:px-12">
          <div className="max-w-xl">
            <h2 className="font-display text-3xl font-semibold tracking-tight">See where your business should transform next.</h2>
            <p className="mt-2 text-primary-foreground/70">Start free, or explore the full experience with our demonstration company.</p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row">
            <Button size="lg" className="h-12 bg-accent px-7 text-accent-foreground hover:bg-accent/90" onClick={() => navigate("/register")} data-testid="cta-start-btn">
              Start Your Assessment <ArrowRight className="h-4 w-4" />
            </Button>
            <Button size="lg" variant="outline" className="h-12 border-primary-foreground/30 bg-transparent px-7 text-primary-foreground hover:bg-primary-foreground/10" onClick={startDemo} disabled={demoLoading}>
              Explore the Demo
            </Button>
          </div>
        </div>
      </section>

      <footer className="border-t border-border bg-background">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-4 px-4 py-8 sm:flex-row sm:items-center sm:px-8 lg:px-12">
          <OrbitWordmark subtle size={22} />
          <p className="text-xs text-muted-foreground">© {new Date().getFullYear()} Mythos India Studios Private Limited. MYTHIVA™ is a trademark of Mythos India Studios Private Limited. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
